import React, { useState, useRef, useEffect } from 'react'
import { Routes } from '@/assets/configs'
import { useRouter } from 'next/router'

import { Box, Skeleton } from '@mui/material'
import NextImage from 'next/image'
import useCurrentUser from '@/hooks/useCurrentUser'
import { useIsQueryOwner } from '@/hooks/index'

import { EmtTypography, EmtIcon, EmtAvatar, EmtRating, EmtSeparator, useMobile, EmtButton } from 'emt-components-v2'
import { styled } from '@mui/material/styles'
import VariantImages from '@/assets/VariantImages'
import { formatPrice } from '@/utils/formatPrice'

import { EmtTheme } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { ReservationDates } from '@/components/PetSitter/ReservationCard/ReservationDates'
import GenericBox from '@/components/PetSitter/GenericBox'
import { InfoTextBox } from '@/components/PetSitter/ReservationCard/InfoTextBox'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'

import { getPetsSummary, getPetsNumber } from '@/pages/pet-sitter/search'

import EditServiceModal from '@/components/pages/pet-sitter/reservations/NewRequest/EditServiceModal'
import EditPetsModal from '@/components/pages/pet-sitter/reservations/NewRequest/EditPetsModal'
import AnimalIncrement from '@/components/PetSitter/AnimalIncrement'

import PetsittersSelectionButtons from '@/components/PetSitter/PetsittersSelection/PetsittersSelectionButtons'

import { cancellationPoliciesMapping } from '@/data/PetSitter/Messages'

import { getRejectedInfoText, getIndisponibilityInfoText } from '@/components/PetSitter/PetSitterCard/PetSitterCard'

import { usePetsitterPrice } from '@/hooks/usePetsitterPrice'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'

// Store
import { shallow } from 'zustand/shallow'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'

// API
import { useQuery } from '@tanstack/react-query'
import { CartData, checkCartAsync } from '@/modules/Cart/CartServiceHelper'

// Data
import { PriceUnitSummary } from '@/components/PetSitter/PriceUnitSummary'
import { PriceIncludedTooltip } from './PriceIncludedTooltip'

export type PricingSummary = {
  total: number
  subtotal: number
  reduction: number | null
  quantity: number
  unitPrice: number
  productLabel: string
  completeProductLabel: string
  icon: string
  cancellationPolicies: string | null
} | null

type AskSitterCardProps = {
  active?: boolean
  shadows?: boolean
  checkbox?: boolean
  alert?: boolean
  data: any
  rejectedReason?: string
  variant?: 'default' | 'view'
  onScrollToSection?: (sectionId: string) => void
  setSignUpModalOpen?: () => void
}

const StyledInlineCard = styled(Card)(
  ({
    theme,
    active,
    isMobile,
    shadows,
  }: {
    theme?: EmtTheme
    active?: boolean
    isMobile?: boolean
    shadows?: boolean
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    // overflowY: 'auto',
    width: '100%',
    padding: '16px 8px',
    gap: '12px',
    borderRadius: '18px',
    border: isMobile && shadows ? '1px solid #E8E8EA' : 'none',
    boxSizing: 'border-box',
    backgroundColor: active ? '#FBF1FE' : 'none',
    boxShadow: shadows ? (isMobile ? 'unset' : '0px 0px 5px 1px rgba(83,83,83,0.10)') : 'unset',
    '& .MuiAvatar-root': {
      width: '100%',
      height: 'auto',
      aspectRatio: '1',
    },
    '&:hover': {
      boxShadow: shadows ? '0px 0px 5px 1px rgba(83,83,83,0.10)' : 'unset',
      '& a': {
        color: theme?.palette.primary.main,
      },
    },
    '& a': {
      textDecoration: 'none',
    },
  }),
)

const StyledInlineCardContent = styled(CardContent)(({ theme }: { theme?: EmtTheme }) => ({
  padding: '8px 12px',
  paddingBottom: '12px !important',
  '& a': {
    '&:hover': {
      color: theme?.palette.primary.main,
    },
    color: theme?.palette.greyGradient.main,
    textDecoration: 'none',
  },
  [theme?.breakpoints.down('md')]: {
    padding: '8px 8px',
  },
}))

const AskSitterCard: React.FC<AskSitterCardProps> = ({
  data,
  variant = 'default',
  rejectedReason,
  shadows = true,
  onScrollToSection,
  setSignUpModalOpen,
  ...props
}) => {
  const { ...rest } = props
  const [isMobile] = useMobile()
  const router = useRouter()
  const { t } = useTranslation('petsitter')
  const { currentUser } = useCurrentUser()
  const [editServiceOpen, setEditServiceOpen] = useState(false)
  const [, , appendQueryType] = useIsQueryOwner()

  const [isPetsOpen, setIsPetsOpen] = useState(false)
  const [isPetsModalOpen, setIsPetsModalOpen] = useState(false)

  const petsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (petsRef.current && !petsRef.current.contains(event.target as Node) && !isMobile) {
        setIsPetsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const cartPayload = useNewRequestCartStore((s) => s.getCartPayload())
  const selectedService = useNewRequestCartStore((s) => s.selectedService)
  const addPetsitter = useNewRequestCartStore((s) => s.addPetsitter)
  const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)
  const icon = useNewRequestCartStore((s) => s.icon)

  const { petsToComplete, setPetsToComplete, setAnimals } = useNewRequestCartStore(
    (s) => ({
      petsToComplete: s.petsToComplete,
      setPetsToComplete: s.setPetsToComplete,
      setAnimals: s.setAnimals,
    }),
    shallow,
  )

  const animalCounts = {
    dog: petsToComplete.find((p) => p.slug === 'dog')?.number ?? 0,
    cat: petsToComplete.find((p) => p.slug === 'cat')?.number ?? 0,
    nac: petsToComplete.find((p) => p.slug === 'nac')?.number ?? 0,
  }

  const handleAnimalChange = (type: 'dog' | 'cat' | 'nac', delta: number) => {
    setPetsToComplete(
      petsToComplete.map((pet) => (pet.slug === type ? { ...pet, number: Math.max(0, pet.number + delta) } : pet)),
    )
    setAnimals([])
  }

  const dates = selectedService
    ? cartPayload?.products?.map(({ offer, ...rest }) => ({
        productSlug: offer,
        ...rest,
      }))
    : undefined

  const { mainPicture, name, distance, reviewsCount, reviewsRating, city, uuid } = data

  const { petsitterPrice, isFetching, reduction, quantity, computedCartPayload } = usePetsitterPrice({
    uuid,
    cartPayload,
    petsToComplete,
  })

  const { data: cartData } = useQuery({
    queryKey: ['check-cart-data', computedCartPayload],
    queryFn: async () => {
      if (!computedCartPayload) return null
      const data = (await checkCartAsync(computedCartPayload)) as CartData
      return data
    },
    enabled: !!computedCartPayload,
    refetchOnWindowFocus: false,
    staleTime: 10000,
  })

  const rejectedInfoText = selectedService
    ? variant === 'view'
      ? getRejectedInfoText(cartData?.rejectedPetsitters?.[uuid]?.reason || rejectedReason, name, selectedService)
      : getIndisponibilityInfoText(
          cartData?.rejectedPetsitters?.[uuid]?.reason || rejectedReason,
          name,
          selectedService,
        )
    : null

  const isRejected = !!rejectedReason

  return (
    <>
      <StyledInlineCard {...rest} isMobile={isMobile} shadows={shadows}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <StyledInlineCardContent
            sx={{
              flexGrow: 1,
              padding: '0px !important',
              display: 'flex',
              flexDirection: 'column',
              gap: '17px',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                padding: '0px !important',
                display: 'flex',
                flexDirection: 'column',
                gap: '17px',
              }}
            >
              {variant === 'view' && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    padding: '0 8px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '12px',
                      filter: isRejected ? 'grayscale(1)' : 'none',
                      opacity: isRejected ? 0.5 : 1,
                    }}
                  >
                    <Box
                      className="EmtInlineCard-image-wrapper"
                      sx={{
                        position: 'relative',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <EmtAvatar
                        sx={{ flexShrink: 0, height: '100%!important', width: '100%' }}
                        size="large"
                        variant="rounded"
                        src={mainPicture.imageThumbnailUrl}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Box sx={{ display: 'flex', gap: '3px', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', gap: '0.3rem' }}>
                          {name && (
                            <EmtTypography
                              variant="h5"
                              fontWeight="800"
                              noWrap
                              sx={{
                                textOverflow: 'ellipsis',
                                maxWidth: '120px',
                              }}
                            >
                              <>{name} </>
                            </EmtTypography>
                          )}
                          <NextImage width={42} height={20} src={VariantImages.Sitter} alt="sitter" />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {city && !currentUser ? (
                            <Box
                              sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center' }}
                              onClick={() => {
                                onScrollToSection?.('map-section')
                              }}
                              title={city || distance || ''}
                            >
                              <EmtIcon
                                className="emt-pin-1"
                                sx={{ color: 'greyGradient.light', fontWeight: 600 }}
                                fontSize="small"
                              />
                              <EmtTypography variant="body2" color="greyGradient.light" fontWeight={600} lineHeight={1}>
                                {city}
                              </EmtTypography>
                            </Box>
                          ) : distance !== undefined && distance !== null && currentUser ? (
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                              onClick={() => {
                                onScrollToSection?.('map-section')
                              }}
                            >
                              <EmtIcon
                                className="emt-pin-1"
                                sx={{ color: 'greyGradient.light', fontWeight: 600 }}
                                fontSize="small"
                              />
                              <EmtTypography
                                variant="caption"
                                color="greyGradient.light"
                                fontWeight={600}
                                lineHeight={0}
                              >
                                {Math.round(distance) >= 1
                                  ? `${Math.round(distance)} km`
                                  : `${(Math.round(distance * 10) / 10).toFixed(1)} km`}
                              </EmtTypography>
                            </Box>
                          ) : null}
                          {reviewsCount > 0 && reviewsRating && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2.5px',
                                marginLeft: '8px',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                onScrollToSection?.('reviews')
                              }}
                            >
                              <EmtRating
                                defaultValue={reviewsRating ? reviewsRating : undefined}
                                label={reviewsRating}
                                labelFontSize="caption"
                                labelFontColor="greyGradient.light"
                                max={1}
                                readOnly
                                size="small"
                                boxSx={{ display: 'flex', alignItems: 'center' }}
                              />
                              <EmtTypography
                                variant="caption"
                                color="greyGradient.light"
                                fontWeight={400}
                                fontSize={10}
                                lineHeight={0}
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                ({t('petSitterCard.review', { count: reviewsCount })})
                              </EmtTypography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {variant === 'view' && (
                    <Box
                      sx={{
                        backgroundColor: '#F6F6F6',
                        borderRadius: '12px',
                        width: '37px',
                        height: '37px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        removePetsitter(uuid)
                      }}
                    >
                      <EmtIcon
                        className="emt-x"
                        sx={{
                          fontSize: '16px',
                          color: '#1D192980',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {!rejectedInfoText && !!petsitterPrice && variant === 'view' && (
                <Box sx={{ padding: '0 8px' }}>
                  <EmtSeparator />
                </Box>
              )}
              {(variant !== 'view' || !!petsitterPrice) && (
                <>
                  <Box
                    sx={{
                      flexGrow: 1,
                      gap: '17px',
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '0 8px',
                    }}
                  >
                    {variant !== 'view' && (
                      <>
                        <EmtTypography variant="body1" fontWeight={700} fontSize="18px">
                          {!!petsitterPrice ? `Votre besoin :` : 'Complétez pour obtenir un tarif :'}
                        </EmtTypography>
                        {dates && dates.length > 0 ? (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setEditServiceOpen(!editServiceOpen)
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <ReservationDates
                              variant="showService"
                              dates={dates as any[]}
                              icon={icon || 'emt-calendar'}
                              actionText={() => setEditServiceOpen(!editServiceOpen)}
                            />
                          </Box>
                        ) : (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setEditServiceOpen(!editServiceOpen)
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <GenericBox
                              reverseText
                              subtitle={'Ajouter un service'}
                              title={'Type de service'}
                              icon="emt-sit-home"
                              actionText={() => setEditServiceOpen(!editServiceOpen)}
                              textActionText="Ajouter"
                            />
                          </Box>
                        )}
                        <Box ref={petsRef} sx={{ position: 'relative' }}>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              isMobile ? setIsPetsModalOpen((prev) => !prev) : setIsPetsOpen((prev) => !prev)
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <GenericBox
                              reverseText
                              subtitle={`Pour quels animaux`}
                              title={getPetsSummary(petsToComplete, t) || 'Sélectionner'}
                              icon="emt-dog"
                              actionText={() => {}}
                            />
                          </Box>
                          {isPetsOpen && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                width: '100%',
                                zIndex: 1,
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                              }}
                            >
                              <Box sx={{ p: 2, minWidth: '260px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  <AnimalIncrement
                                    slug="dog"
                                    count={animalCounts.dog}
                                    onChange={(delta) => handleAnimalChange('dog', delta)}
                                  />
                                  <AnimalIncrement
                                    slug="cat"
                                    count={animalCounts.cat}
                                    onChange={(delta) => handleAnimalChange('cat', delta)}
                                  />
                                  <AnimalIncrement
                                    slug="nac"
                                    count={animalCounts.nac}
                                    onChange={(delta) => handleAnimalChange('nac', delta)}
                                  />
                                  <EmtButton
                                    fullWidth
                                    color="purple"
                                    variant="contained"
                                    onClick={() => setIsPetsOpen(false)}
                                  >
                                    Valider
                                  </EmtButton>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {isFetching && (
                      <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: '16px' }} />
                    )}

                    {reduction && !isFetching && !rejectedInfoText && (
                      <>
                        {variant !== 'view' && <EmtSeparator />}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <EmtTypography variant="body2" color="#18BA92" fontWeight={400} textAlign="center">
                            <b>Réduction</b> (Membre Premium)
                          </EmtTypography>
                          <EmtTypography variant="body2" color="#18BA92" fontWeight={700} textAlign="center">
                            -{formatPrice(reduction)}€
                          </EmtTypography>
                        </Box>
                        <EmtSeparator />
                      </>
                    )}

                    {!!petsitterPrice && !isFetching && !rejectedInfoText && (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {' '}
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}
                        >
                          <EmtTypography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              Total
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PriceIncludedTooltip />
                              </Box>
                            </Box>
                          </EmtTypography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {reduction && (
                              <EmtTypography variant="body2" sx={{ textDecoration: 'line-through' }}>
                                {formatPrice(petsitterPrice.total + reduction)}€
                              </EmtTypography>
                            )}
                            <EmtTypography
                              variant="body2"
                              fontWeight={700}
                              textAlign="center"
                              fontSize="18px"
                              sx={{ color: reduction ? '#18BA92' : 'inherit' }}
                            >
                              {formatPrice(petsitterPrice.total)}€
                            </EmtTypography>
                          </Box>
                        </Box>{' '}
                        <PriceUnitSummary
                          total={petsitterPrice.total}
                          quantity={quantity}
                          productSlug={petsitterPrice?.orderDetails?.[0]?.productSlug}
                          petsNumber={getPetsNumber(petsToComplete)}
                        />
                      </Box>
                    )}

                    {rejectedInfoText && <InfoTextBox infoText={rejectedInfoText} />}
                    {variant !== 'view' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <PetsittersSelectionButtons id={uuid} selectClickOverride={setSignUpModalOpen} />
                        <EmtButton
                          fullWidth
                          color="purple"
                          variant="outlined"
                          startIcon={
                            <Box
                              sx={{
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                width: '14px',
                                height: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <EmtIcon
                                className="emt-tick-circle-1"
                                sx={{
                                  fontSize: '17px',
                                  color: '#AE59C6',
                                }}
                              />
                            </Box>
                          }
                          onClick={() => {
                            if (setSignUpModalOpen) {
                              setSignUpModalOpen()
                            } else {
                              addPetsitter(uuid)
                              // Send gtm event when a pet sitter is added to the selection
                              gtmSendCustomEvent('member_add_to_selection_sst', {
                                member_role_variable: 'maitre',
                                member_id_variable: currentUser?.id || 'unknown',
                                brand_variable: Constants.storageID,
                                website_variable: Constants.rootUrl,
                              })
                              router.push(appendQueryType(Routes.PetSitter.Reservations.NewRequest))
                            }
                          }}
                        >
                          Réserver {name}
                        </EmtButton>
                      </Box>
                    )}
                    {petsitterPrice?.cancellationPolicies && !rejectedInfoText && (
                      <EmtTypography variant="caption" color="greyGradient.light" fontWeight={400} textAlign="center">
                        <b>Annulation gratuite</b>{' '}
                        {(
                          cancellationPoliciesMapping.find((item) => item.slug === petsitterPrice?.cancellationPolicies)
                            ?.title || ''
                        ).toLowerCase()}
                        .
                      </EmtTypography>
                    )}
                  </Box>
                </>
              )}
              {variant === 'view' && rejectedInfoText && !petsitterPrice && <InfoTextBox infoText={rejectedInfoText} />}
            </Box>
          </StyledInlineCardContent>
        </Box>
      </StyledInlineCard>
      {/* Edit Service Modal */}
      <EditServiceModal open={editServiceOpen} setOpen={setEditServiceOpen} />
      <EditPetsModal
        open={isPetsModalOpen}
        onClose={() => setIsPetsModalOpen(false)}
        animalCounts={animalCounts}
        onChange={handleAnimalChange}
      />
    </>
  )
}

export default AskSitterCard
